import { useState, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSupabaseClient } from "@/lib/supabase";
import { ArrowsClockwise as RefreshCw, MagnifyingGlass as Search, CaretDown as ChevronDown, CaretRight as ChevronRight, Trash as Trash2, WarningCircle as AlertCircle, Copy, Check, ArrowsOut as Maximize2, X } from "@phosphor-icons/react";
import type { ProviderCall } from "../api/adminLogs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { LoadingState, ErrorState } from "@/components/states";
import { fetchRequestLogs, clearOldLogs, type LogsFilter, type RequestLog } from "../api/adminLogs";

// ── JSON block with copy + expand ─────────────────────────────────────────

interface JsonModalProps {
  title: string;
  json: string;
  onClose: () => void;
}

function JsonModal({ title, json, onClose }: JsonModalProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(json).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }, [json]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-card border border-border rounded-lg shadow-2xl w-full max-w-5xl max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border/60">
          <p className="text-xs font-medium text-foreground/80 font-mono">{title}</p>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={handleCopy}>
              {copied
                ? <><Check className="w-3.5 h-3.5 mr-1 text-green-400" />Copiado</>
                : <><Copy className="w-3.5 h-3.5 mr-1" />Copiar</>}
            </Button>
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={onClose}>
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-4 min-h-0">
          <pre className="text-xs font-mono text-foreground/85 whitespace-pre-wrap break-all leading-relaxed">{json}</pre>
        </div>
      </div>
    </div>
  );
}

interface JsonBlockProps {
  label: string;
  data: unknown;
  borderClass?: string;
}

function JsonBlock({ label, data, borderClass = "border-border/50" }: JsonBlockProps) {
  const [copied, setCopied] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const json = JSON.stringify(data, null, 2);

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(json).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }, [json]);

  return (
    <>
      {expanded && <JsonModal title={label} json={json} onClose={() => setExpanded(false)} />}
      <div>
        <div className="flex items-center gap-2 mb-1">
          <span className="text-[10px] text-muted-foreground uppercase tracking-wide shrink-0">{label}</span>
          <button
            onClick={handleCopy}
            className="flex items-center gap-1 text-[11px] font-medium text-foreground/60 bg-muted/60 border border-border/40 hover:bg-muted hover:text-foreground px-2 py-0.5 rounded transition-colors shrink-0"
          >
            {copied ? <Check className="w-3 h-3 text-green-400" /> : <Copy className="w-3 h-3" />}
            {copied ? "Copiado" : "Copiar"}
          </button>
          <button
            onClick={() => setExpanded(true)}
            className="flex items-center gap-1 text-[11px] font-medium text-foreground/60 bg-muted/60 border border-border/40 hover:bg-muted hover:text-foreground px-2 py-0.5 rounded transition-colors shrink-0"
          >
            <Maximize2 className="w-3 h-3" />
            Expandir
          </button>
        </div>
        <pre className={`text-xs bg-background border ${borderClass} rounded p-3 overflow-y-auto max-h-72 font-mono text-foreground/80 whitespace-pre-wrap break-all`}>
          {json}
        </pre>
      </div>
    </>
  );
}

const FUNCTIONS = ["all", "llm-proxy", "simulator", "admin-config", "admin-agents", "byok-keys", "auth-exchange"];
const STATUS_CLASSES = ["all", "2xx", "4xx", "5xx"] as const;

function statusBadge(status: number) {
  if (status < 300) return <Badge className="bg-green-500/15 text-green-400 border-green-500/30">{status}</Badge>;
  if (status < 400) return <Badge className="bg-blue-500/15 text-blue-400 border-blue-500/30">{status}</Badge>;
  if (status < 500) return <Badge className="bg-warning/15 text-yellow-400 border-warning/30">{status}</Badge>;
  return <Badge className="bg-destructive/15 text-red-400 border-destructive/30">{status}</Badge>;
}

function methodBadge(method: string) {
  const colors: Record<string, string> = {
    GET:    "bg-sky-500/15 text-sky-400 border-sky-500/30",
    POST:   "bg-violet-500/15 text-violet-400 border-violet-500/30",
    PUT:    "bg-amber-500/15 text-amber-400 border-amber-500/30",
    DELETE: "bg-rose-500/15 text-rose-400 border-rose-500/30",
  };
  return <Badge className={colors[method] ?? "bg-muted text-muted-foreground"}>{method}</Badge>;
}

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

function formatTs(iso: string): string {
  return new Date(iso).toLocaleString("pt-BR", {
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
  });
}

interface LogRowProps {
  log: RequestLog;
}

function LogRow({ log }: LogRowProps) {
  const [expanded, setExpanded] = useState(false);

  return (
    <>
      <tr
        className="border-b border-border/40 hover:bg-muted/30 cursor-pointer transition-colors"
        onClick={() => setExpanded((p) => !p)}
      >
        <td className="px-3 py-2 w-4">
          {expanded
            ? <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />
            : <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />}
        </td>
        <td className="px-3 py-2 text-xs text-muted-foreground whitespace-nowrap font-mono">
          {formatTs(log.created_at)}
        </td>
        <td className="px-3 py-2">{methodBadge(log.method)}</td>
        <td className="px-3 py-2">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-xs font-mono text-foreground/80 bg-muted px-1.5 py-0.5 rounded">
              {log.function_name}
            </span>
            {log.metadata?.feature ? (
              <span className="inline-flex items-center rounded bg-primary/15 border border-primary/30 px-1.5 py-0.5 text-[10px] font-mono text-primary">
                {String(log.metadata.feature)}
              </span>
            ) : null}
          </div>
        </td>
        <td className="px-3 py-2 text-xs text-muted-foreground font-mono max-w-[200px] truncate">
          {log.path}
        </td>
        <td className="px-3 py-2">{statusBadge(log.status)}</td>
        <td className="px-3 py-2 text-xs text-muted-foreground whitespace-nowrap">
          {formatDuration(log.duration_ms)}
        </td>
        <td className="px-3 py-2 text-xs max-w-[220px] truncate">
          {log.user_email ? (
            <div className="flex flex-col leading-tight">
              {log.user_display_name ? (
                <span className="text-foreground/90 truncate">{log.user_display_name}</span>
              ) : null}
              <span className="text-muted-foreground font-mono truncate">{log.user_email}</span>
            </div>
          ) : log.user_id ? (
            <span className="text-muted-foreground font-mono truncate" title={log.user_id}>
              {log.user_id}
            </span>
          ) : (
            <span className="text-muted-foreground">—</span>
          )}
        </td>
        {log.error && (
          <td className="px-3 py-2">
            <span className="flex items-center gap-1 text-xs text-red-400">
              <AlertCircle className="w-3 h-3" />
              {log.error.slice(0, 40)}
            </span>
          </td>
        )}
      </tr>
      {expanded && (
        <tr className="border-b border-border/40 bg-muted/20">
          <td colSpan={9} className="px-6 py-4">
            <div className="space-y-4">
              {/* Basic info */}
              <div className="grid grid-cols-2 gap-x-8 gap-y-1.5 text-xs">
                <div><span className="text-muted-foreground">ID:</span> <span className="font-mono text-foreground/70">{log.id}</span></div>
                <div><span className="text-muted-foreground">IP:</span> <span className="font-mono">{log.ip ?? "—"}</span></div>
                {(log.user_email || log.user_display_name) && (
                  <div className="col-span-2">
                    <span className="text-muted-foreground">Usuário:</span>{" "}
                    {log.user_display_name ? (
                      <span className="text-foreground/90">{log.user_display_name} </span>
                    ) : null}
                    {log.user_email ? (
                      <span className="font-mono">{log.user_email}</span>
                    ) : null}
                  </div>
                )}
                {log.user_id && (
                  <div className="col-span-2"><span className="text-muted-foreground">User ID:</span> <span className="font-mono text-foreground/60">{log.user_id}</span></div>
                )}
                {log.error && (
                  <div className="col-span-2"><span className="text-muted-foreground">Error:</span> <span className="text-red-400 font-mono">{log.error}</span></div>
                )}
              </div>

              {/* AI call metadata (model, tokens, cost) */}
              {log.metadata && (
                <div>
                  <p className="text-xs font-medium text-foreground/60 mb-2 uppercase tracking-wide">AI Call</p>
                  {/* Feature attribution — surfaces WHICH functionality fired this call */}
                  {(log.metadata.feature || log.metadata.tipo_slug || log.metadata.objetivo_slug) && (
                    <div className="flex flex-wrap items-center gap-1.5 mb-2">
                      {log.metadata.feature && (
                        <span className="inline-flex items-center rounded bg-primary/15 border border-primary/30 px-2 py-0.5 text-[11px] font-mono text-primary">
                          {String(log.metadata.feature)}
                        </span>
                      )}
                      {log.metadata.tipo_slug && (
                        <span className="inline-flex items-center rounded bg-muted border border-border/40 px-2 py-0.5 text-[11px] text-foreground/70">
                          tipo: {String(log.metadata.tipo_slug)}
                        </span>
                      )}
                      {log.metadata.objetivo_slug && (
                        <span className="inline-flex items-center rounded bg-muted border border-border/40 px-2 py-0.5 text-[11px] text-foreground/70">
                          objetivo: {String(log.metadata.objetivo_slug)}
                        </span>
                      )}
                    </div>
                  )}
                  <div className="grid grid-cols-3 gap-3">
                    {log.metadata.model_id && (
                      <div className="bg-background border border-border/50 rounded p-2.5">
                        <p className="text-[10px] text-muted-foreground uppercase tracking-wide mb-0.5">Modelo</p>
                        <p className="text-xs font-mono font-medium">{String(log.metadata.model_id)}</p>
                      </div>
                    )}
                    {log.metadata.provider && (
                      <div className="bg-background border border-border/50 rounded p-2.5">
                        <p className="text-[10px] text-muted-foreground uppercase tracking-wide mb-0.5">Provider</p>
                        <p className="text-xs font-mono font-medium capitalize">{String(log.metadata.provider)}</p>
                      </div>
                    )}
                    {log.metadata.prompt_config_id && (
                      <div className="bg-background border border-border/50 rounded p-2.5">
                        <p className="text-[10px] text-muted-foreground uppercase tracking-wide mb-0.5">Prompt Config</p>
                        <p className="text-xs font-mono">{String(log.metadata.prompt_config_id)}{log.metadata.prompt_version != null ? ` v${log.metadata.prompt_version}` : ""}</p>
                      </div>
                    )}
                    {log.metadata.tokens_in != null && (
                      <div className="bg-background border border-border/50 rounded p-2.5">
                        <p className="text-[10px] text-muted-foreground uppercase tracking-wide mb-0.5">Tokens entrada</p>
                        <p className="text-xs font-mono font-medium">{Number(log.metadata.tokens_in).toLocaleString()}</p>
                      </div>
                    )}
                    {log.metadata.tokens_out != null && (
                      <div className="bg-background border border-border/50 rounded p-2.5">
                        <p className="text-[10px] text-muted-foreground uppercase tracking-wide mb-0.5">Tokens saída</p>
                        <p className="text-xs font-mono font-medium">{Number(log.metadata.tokens_out).toLocaleString()}</p>
                      </div>
                    )}
                    {log.metadata.cost_usd != null && (
                      <div className="bg-background border border-border/50 rounded p-2.5">
                        <p className="text-[10px] text-muted-foreground uppercase tracking-wide mb-0.5">Custo</p>
                        <p className="text-xs font-mono font-medium text-green-400">
                          ${Number(log.metadata.cost_usd).toFixed(6)}
                        </p>
                      </div>
                    )}
                    {log.metadata.job_id && (
                      <div className="bg-background border border-border/50 rounded p-2.5 col-span-3">
                        <p className="text-[10px] text-muted-foreground uppercase tracking-wide mb-0.5">Job ID</p>
                        <p className="text-xs font-mono text-foreground/70">{String(log.metadata.job_id)}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Full provider audit: raw HTTP request body + raw response */}
              {log.provider_payload && (
                <div className="space-y-3">
                  <p className="text-xs font-medium text-foreground/60 uppercase tracking-wide">Provider Audit</p>
                  {log.provider_payload.calls && log.provider_payload.calls.length > 0 ? (
                    <div className="space-y-4">
                      {log.provider_payload.calls.map((call: ProviderCall, i: number) => (
                        <div key={i} className="border border-border/40 rounded p-3 space-y-3">
                          <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">
                            {call.step ? `Step: ${call.step}` : `Call ${i + 1}`}
                            {call.url && (
                              <span className="ml-2 font-mono normal-case font-normal">
                                POST {call.url}
                              </span>
                            )}
                          </p>
                          {call.request && (
                            <JsonBlock label="Request Body" data={call.request} />
                          )}
                          {call.response && (
                            <JsonBlock label="Response Body" data={call.response} borderClass="border-emerald-500/20" />
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <>
                      {log.provider_payload.url && (
                        <p className="text-muted-foreground font-mono text-xs">POST {log.provider_payload.url}</p>
                      )}
                      {log.provider_payload.request && (
                        <JsonBlock label="Request Body" data={log.provider_payload.request} />
                      )}
                      {log.provider_payload.response && (
                        <JsonBlock label="Response Body" data={log.provider_payload.response} borderClass="border-emerald-500/20" />
                      )}
                    </>
                  )}
                </div>
              )}

              {/* Incoming request body (client → edge function) */}
              {log.request_body && (
                <JsonBlock label="Request Body (cliente)" data={log.request_body} />
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

export function LogsSection() {
  const [fnFilter, setFnFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [clearing, setClearing] = useState(false);

  const { client: authClient, isReady } = useSupabaseClient();

  const filter: LogsFilter = {
    function_name: fnFilter !== "all" ? fnFilter : undefined,
    status_class: statusFilter !== "all" ? (statusFilter as LogsFilter["status_class"]) : undefined,
  };

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ["admin", "request_logs", filter],
    queryFn: () => fetchRequestLogs(authClient, filter),
    enabled: isReady,
    staleTime: 0,
  });

  const handleClear = useCallback(async () => {
    if (!window.confirm("Apagar logs com mais de 30 dias?")) return;
    setClearing(true);
    try {
      const n = await clearOldLogs(authClient, 30);
      await refetch();
      alert(`${n} log(s) removido(s).`);
    } finally {
      setClearing(false);
    }
  }, [authClient, refetch]);

  const rows = (data ?? []).filter((log) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      log.path.toLowerCase().includes(q) ||
      (log.user_email ?? "").toLowerCase().includes(q) ||
      (log.user_display_name ?? "").toLowerCase().includes(q) ||
      (log.user_id ?? "").toLowerCase().includes(q) ||
      (log.error ?? "").toLowerCase().includes(q) ||
      (String(log.metadata?.feature ?? "")).toLowerCase().includes(q) ||
      log.function_name.toLowerCase().includes(q)
    );
  });

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <Select value={fnFilter} onValueChange={setFnFilter}>
            <SelectTrigger className="w-44 h-8 text-xs">
              <SelectValue placeholder="Função" />
            </SelectTrigger>
            <SelectContent>
              {FUNCTIONS.map((f) => (
                <SelectItem key={f} value={f} className="text-xs">
                  {f === "all" ? "Todas as funções" : f}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-28 h-8 text-xs">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              {STATUS_CLASSES.map((s) => (
                <SelectItem key={s} value={s} className="text-xs">
                  {s === "all" ? "Todos" : s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
            <Input
              className="pl-7 h-8 text-xs w-56"
              placeholder="Buscar email, feature, path, erro..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">{rows.length} registros</span>
          <Button
            variant="ghost"
            size="sm"
            className="h-8 px-2 text-xs"
            onClick={() => refetch()}
            disabled={isFetching}
          >
            <RefreshCw className={`w-3.5 h-3.5 mr-1 ${isFetching ? "animate-spin" : ""}`} />
            Atualizar
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-8 px-2 text-xs text-muted-foreground hover:text-destructive"
            onClick={handleClear}
            disabled={clearing}
          >
            <Trash2 className="w-3.5 h-3.5 mr-1" />
            Limpar antigos
          </Button>
        </div>
      </div>

      {/* Table */}
      {isLoading && <LoadingState />}
      {error && <ErrorState message="Erro ao carregar logs." />}
      {!isLoading && !error && (
        <ScrollArea className="h-[600px] rounded-lg border border-border/50">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-card z-10">
              <tr className="border-b border-border/60 text-xs text-muted-foreground">
                <th className="px-3 py-2 w-4" />
                <th className="px-3 py-2 text-left whitespace-nowrap">Horário</th>
                <th className="px-3 py-2 text-left">Método</th>
                <th className="px-3 py-2 text-left">Função</th>
                <th className="px-3 py-2 text-left">Path</th>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2 text-left">Duração</th>
                <th className="px-3 py-2 text-left">Usuário</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-6 py-12 text-center text-sm text-muted-foreground">
                    Nenhum log encontrado.
                  </td>
                </tr>
              ) : (
                rows.map((log) => <LogRow key={log.id} log={log} />)
              )}
            </tbody>
          </table>
        </ScrollArea>
      )}
    </div>
  );
}
